import { useSyncExternalStore } from "react";
import { isStandalone } from "@/lib/push";

// install prompt + service worker update state, shared across the app
let state = { canPrompt: false, installed: isStandalone(), updateReady: false };
let deferred = null;
let waiting = null;
let accepted = false;
const subs = new Set();
const set = (patch) => { state = { ...state, ...patch }; subs.forEach((f) => f()); };

window.addEventListener("beforeinstallprompt", (e) => {
  e.preventDefault();
  deferred = e;
  set({ canPrompt: true });
});
window.addEventListener("appinstalled", () => { deferred = null; set({ canPrompt: false, installed: true }); });

export const usePwa = () => useSyncExternalStore((f) => { subs.add(f); return () => subs.delete(f); }, () => state);

// opens the native install dialog; false when the browser does not offer it
export async function promptInstall() {
  if (!deferred) return false;
  const e = deferred;
  deferred = null;
  e.prompt();
  const { outcome } = await e.userChoice;
  set({ canPrompt: false, installed: state.installed || outcome === "accepted" });
  return true;
}

export function applyUpdate() {
  accepted = true;
  waiting?.postMessage("SKIP_WAITING");
}

export function registerServiceWorker() {
  if (!("serviceWorker" in navigator) || !window.isSecureContext) return;
  // a fresh open (new tab or app launch) applies a waiting version before the person starts writing
  const freshOpen = !sessionStorage.getItem("pensive_opened");
  sessionStorage.setItem("pensive_opened", "1");
  let typed = false;
  const markTyped = () => { typed = true; };
  window.addEventListener("keydown", markTyped, { once: true, capture: true });
  window.addEventListener("input", markTyped, { once: true, capture: true });
  navigator.serviceWorker.addEventListener("controllerchange", () => { if (accepted) window.location.reload(); });
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js", { scope: "/" }).then((reg) => {
      const markWaiting = (w) => { waiting = w; set({ updateReady: true }); };
      if (reg.waiting && navigator.serviceWorker.controller) {
        markWaiting(reg.waiting);
        if (freshOpen && !typed) applyUpdate();
      }
      reg.addEventListener("updatefound", () => {
        const w = reg.installing;
        w?.addEventListener("statechange", () => {
          if (w.state === "installed" && navigator.serviceWorker.controller) markWaiting(w);
        });
      });
      setInterval(() => reg.update().catch(() => {}), 60 * 60 * 1000);
    }).catch(() => {});
  });
}

const ua = () => navigator.userAgent;
export const isAndroid = () => /Android/i.test(ua());
export const isIOSSafari = () => /Safari/i.test(ua()) && !/CriOS|FxiOS|EdgiOS|GSA|OPiOS|YaBrowser|DuckDuckGo/i.test(ua());
export const isMacSafari = () => /Macintosh/i.test(ua()) && navigator.maxTouchPoints <= 1
  && /Safari/i.test(ua()) && !/Chrome|Chromium|Edg|Firefox/i.test(ua());
